// --- ShopperStripe.js (updated) ---
// Behavior:
//  • Desktop: PaymentElement uses 'tabs' layout (unchanged)
//  • Mobile (≤600px): switches to 'accordion' so methods stack one-per-row
//  • Also slightly shrinks fonts/spacing on mobile for more compact tabs

import React, { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  // PaymentRequestButtonElement, // (optional UI render if you want to show the wallet button)
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { Button, Box, Typography, CircularProgress } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function _CheckoutForm({
  amount,
  processingFee,
  items = [],
  shippingCost = 0,
  email,
  phoneNumber,
  shippingAddress,
  weight,
  voucherCode,
  clientSecret,
  skipOrder = false,         // when true, don't create order or redeem voucher or redirect
  onSuccess,                 // callback(paymentIntent) on success when skipOrder
  onError,                   // callback(error) on failure when skipOrder
  successRedirect,           // override redirect URL when not skipOrder
}) {
  const stripe = useStripe();
  const elements = useElements();

  const [pr, setPr] = useState(null);     // PaymentRequest instance
  const [prReady, setPrReady] = useState(false);

  // ▶︎ Responsive layout: tabs on desktop, accordion on mobile to stack methods
  const isMobile = useMediaQuery('(max-width:600px)');
  const paymentLayout = isMobile ? 'accordion' : 'tabs';

  // Shared success flow (used by both Payment Element + Apple/Google Pay)
  const afterSuccess = async (paymentIntent) => {
    if (skipOrder) {
      onSuccess?.(paymentIntent);
      return;
    }

    // fetch full PI to get charge and card details
    const { paymentIntent: fullPI } = await stripe.retrievePaymentIntent(clientSecret);
    const charge = fullPI.charges?.data?.[0];
    const card = charge?.payment_method_details?.card || {};
    const pmType = fullPI.payment_method_types?.[0];
    const { last4, brand, exp_month: expMonth, exp_year: expYear } = card;

    // 1️⃣ Save order
    try {
      await fetch('/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: paymentIntent.id,
          email,
          phoneNumber,
          items: items.map((i) => ({
            ...i,
            subtotal: parseFloat(String(i.price).replace(/[^\d.]/g, '')) * i.qty,
          })),
          shippingCost,
          total: amount,
          processingFee,
          weight,
          paymentMethod: { type: pmType, last4, brand, expMonth, expYear },
          shippingAddress,
          createdAt: Date.now(),
        }),
      });
      // 1.a️⃣ Send order confirmation email
      await fetch('/orders/email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderId: paymentIntent.id,
          to: email,
        }),
      });
    } catch (err) {
      console.error('Order save failed', err);
      // non-blocking
    }

    // 2️⃣ Redeem the voucher so it can’t be used again
    if (voucherCode) {
      try {
        const resp = await fetch(`/giftcards/${voucherCode}/redeem`, { method: 'PATCH' });
        if (!resp.ok) {
          console.error('Redeem failed', await resp.text());
        }
      } catch (err) {
        console.error('Failed to redeem voucher', err);
      }
    }

    // 3️⃣ Redirect to success
    window.location.href =
      successRedirect || `/order-confirmation/success?order_id=${paymentIntent.id}`;
  };

  // Build the Payment Request (Apple/Google Pay) and check availability
  useEffect(() => {
    if (!stripe || !amount) return;

    const totalInCents = Math.round(amount * 100);
    const paymentRequest = stripe.paymentRequest({
      country: 'DE', // adjust to your store country
      currency: 'eur',
      total: { label: 'Marrakech Reserve', amount: totalInCents },
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: !!phoneNumber,
      requestShipping: !!shippingAddress, // set true if you want shipping collection in wallet
    });

    paymentRequest.canMakePayment().then((result) => {
      if (result) {
        setPr(paymentRequest);
        setPrReady(true);
      } else {
        setPr(null);
        setPrReady(false);
      }
    });

    return () => setPr(null);
  }, [stripe, amount, phoneNumber, shippingAddress]);

  // Handle wallet authorization (Apple/Google Pay)
  useEffect(() => {
    if (!pr || !stripe || !clientSecret) return;

    const handler = async (ev) => {
      try {
        // Confirm the existing PaymentIntent with the wallet's payment method
        const { error, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        );

        if (error) {
          ev.complete('fail');
          if (skipOrder && onError) onError(error);
          else window.location.href = '/order-confirmation/failed';
          return;
        }

        // If additional actions (3DS) are required, handle them
        if (paymentIntent.status === 'requires_action') {
          const { error: actionError, paymentIntent: actedPI } =
            await stripe.confirmCardPayment(clientSecret);
          if (actionError) {
            ev.complete('fail');
            if (skipOrder && onError) onError(actionError);
            else window.location.href = '/order-confirmation/failed';
            return;
          }
          await afterSuccess(actedPI);
          ev.complete('success');
          return;
        }

        await afterSuccess(paymentIntent);
        ev.complete('success');
      } catch (err) {
        console.error(err);
        ev.complete('fail');
        if (skipOrder && onError) onError(err);
        else window.location.href = '/order-confirmation/failed';
      }
    };

    pr.on('paymentmethod', handler);
    return () => {
      // Stripe doesn't expose an off() API; re-instantiation on dependency change prevents leaks.
    };
  }, [pr, stripe, clientSecret, skipOrder, onError]);

  // Normal Payment Element submit (cards + EU redirect methods like iDEAL/Sofort/etc.)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    // Ensure browser autofill is applied + Stripe validates fields
    const { error: submitError } = await elements.submit();
    if (submitError) {
      console.error(submitError);
      // Optionally show this in the UI instead of redirecting:
      // setPayError(submitError.message);
      return;
    }

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Needed for redirect methods to come back to your site
      return_url: `${window.location.origin}/order-confirmation/complete`,
        receipt_email: email || undefined,
       payment_method_data: {
       billing_details: {
       email: email || undefined,
       phone: phoneNumber || undefined,
       name: shippingAddress ? `${shippingAddress.firstName} ${shippingAddress.lastName}`.trim() : undefined,
       address: shippingAddress ? {
         line1: shippingAddress.address1 || undefined,
         postal_code: shippingAddress.postalCode || undefined,
         city: shippingAddress.city || undefined,
         country: shippingAddress.country ? shippingAddress.country.toUpperCase().slice(0,2) : undefined,
       } : undefined,
     },
   },

      },
      redirect: 'if_required',
    });

    if (error) {
      console.error(error);
      if (skipOrder && onError) {
        onError(error);
        return;
      }
      window.location.href = '/order-confirmation/failed';
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      await afterSuccess(paymentIntent);
    }
  };

  return (
    <form onSubmit={handleSubmit} autoComplete="on">
      <Box sx={{ mb: 4 }}>
        {/* Payment Element exposes Cards, iDEAL, Bancontact, Sofort, Giropay, EPS, P24, etc. */}
 <PaymentElement
   key={paymentLayout}
   options={{
     layout: paymentLayout,
     defaultValues: {
       billingDetails: {
         email: email || '',
         name: shippingAddress ? `${shippingAddress.firstName} ${shippingAddress.lastName}`.trim() : '',
         phone: phoneNumber || '',
         address: shippingAddress ? {
           line1: shippingAddress.address1 || '',
           postal_code: shippingAddress.postalCode || '',
           city: shippingAddress.city || '',
           country: shippingAddress.country ? shippingAddress.country.toUpperCase().slice(0,2) : '',
         } : undefined,
       },
     },
   }}
 />      </Box>

      <Button type="submit" variant="contained" disabled={!stripe} fullWidth size="large">
        {stripe ? `Pay €${amount?.toFixed(2)}` : <CircularProgress size={20} />}
      </Button>
    </form>
  );
}

export default function ShopperStripe(props) {
  const [clientSecret, setClientSecret] = useState('');

  // Use same breakpoint as _CheckoutForm for visual consistency
  const isMobile = useMediaQuery('(max-width:600px)');

 // We only care about the amount for creating the PI here.
useEffect(() => {
  const controller = new AbortController();

  const run = async () => {
    // 1) Validate amount (Stripe needs integer cents ≥ 1)
    const amountInCents = Math.round(Number(props.amount || 0) * 100);
    console.log('Creating PI for', props.amount, '€ →', amountInCents, 'cents');

    if (!Number.isFinite(amountInCents) || amountInCents < 1) {
      console.warn('Invalid amount for PaymentIntent:', props.amount);
      alert('Please enter a valid amount (≥ €0.01).');
      return;
    }

    // 2) Build payload
    const payload = {
      amount: amountInCents,
      currency: 'eur',
      email: props.email || undefined,
      voucherCode: props.voucherCode || undefined,
      items: props.items || [],
      phoneNumber: props.phoneNumber || undefined,
      shippingAddress: props.shippingAddress || undefined, // { firstName, lastName, address1, city, postalCode, country }
      shippingCost: props.shippingCost,
      processingFee: props.processingFee,
      weight: props.weight,
    };

    // 3) Call server and surface the real error text if it fails
    try {
      const r = await fetch('/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      const raw = await r.text(); // grab text so we can include server error body
      if (!r.ok) {
        throw new Error(`HTTP ${r.status} — ${raw}`);
      }

      const data = JSON.parse(raw);
      if (!data?.clientSecret) throw new Error('No clientSecret returned from server');
      setClientSecret(data.clientSecret);
    } catch (err) {
      if (err.name === 'AbortError') return; // component unmounted
      console.error('PI create failed:', err);
      alert('Payment initialization failed. Please try again.');
    }
  };

  run();
  return () => controller.abort();
  // NOTE: if items/shippingAddress are re-created on every render,
  // memoize them upstream or use JSON.stringify in deps.
}, [
  props.amount,
  props.email,
  props.voucherCode,
  props.items,
  props.phoneNumber,
  props.shippingAddress,
  props.shippingCost,
  props.processingFee,
  props.weight,
]);



  if (!clientSecret) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography>Loading payment form…</Typography>
        <CircularProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  // Slightly tighter appearance on mobile so tabs/labels fit better
  const appearance = isMobile
    ? {
        theme: 'stripe',
        variables: {
          fontSizeBase: '13px',
          spacingUnit: '2px',
          borderRadius: '6px',
        },
        rules: {
          '.TabLabel': { fontSize: '13px' },
        },
      }
    : { theme: 'stripe' };

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance,
        locale: 'de', // or 'en' if you prefer
      }}
    >
      <_CheckoutForm
        {...props}
        clientSecret={clientSecret}
        processingFee={props.processingFee}
      />
    </Elements>
  );
}
